import { logger } from './logger';
import { createLogContext } from './loggerUtils';
import { serializeError } from './errorSerializer';
import { StorageData } from '../types/storage';
import { getKvStore } from '../infrastructure/platform';

/**
 * Quota failures are permanent for the same payload — retrying the identical
 * oversized write is guaranteed to fail again, so they must not be retried.
 */
function isQuotaError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /quota/i.test(message);
}

interface StorageOperation {
  type: 'set' | 'remove';
  key: string;
  value?: any;
  timestamp: number;
  resolve: (value: void | PromiseLike<void>) => void;
  reject: (reason?: any) => void;
}

interface BatchedStorageOptions {
  batchDelay?: number;
  maxBatchSize?: number;
  retryAttempts?: number;
  retryDelay?: number;
}

const DEFAULT_OPTIONS: Required<BatchedStorageOptions> = {
  batchDelay: 50, // 50ms delay to batch operations
  maxBatchSize: 100, // Maximum operations in a batch
  retryAttempts: 3, // Number of retry attempts
  retryDelay: 1000, // 1 second between retries
};

/**
 * Manages batched storage operations for improved performance
 */
export class BatchedStorageManager {
  private static instance: BatchedStorageManager;
  private pendingOperations: StorageOperation[] = [];
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private options: Required<BatchedStorageOptions>;
  private metrics = {
    totalOperations: 0,
    batchedOperations: 0,
    successfulOperations: 0,
    failedOperations: 0,
    averageBatchSize: 0,
    totalBatches: 0,
  };

  private constructor(options: BatchedStorageOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  public static getInstance(options?: BatchedStorageOptions): BatchedStorageManager {
    if (!BatchedStorageManager.instance) {
      BatchedStorageManager.instance = new BatchedStorageManager(options);
    }
    return BatchedStorageManager.instance;
  }

  /**
   * Sets a value in storage with batching
   */
  public async set<T>(key: string, value: StorageData<T>): Promise<void> {
    this.metrics.totalOperations++;
    return new Promise((resolve, reject) => {
      this.queueOperation({
        type: 'set',
        key,
        value,
        timestamp: Date.now(),
        resolve,
        reject,
      });
    });
  }

  /**
   * Removes a value from storage with batching
   */
  public async remove(key: string): Promise<void> {
    this.metrics.totalOperations++;
    return new Promise((resolve, reject) => {
      this.queueOperation({
        type: 'remove',
        key,
        timestamp: Date.now(),
        resolve,
        reject,
      });
    });
  }

  /**
   * Gets a value from storage (not batched since reads are independent)
   * @template T The type of data stored in the StorageData wrapper
   * @param key The key to get from storage
   * @returns The StorageData object or null if not found
   */
  public async get<T = any>(key: string): Promise<StorageData<T> | null> {
    try {
      // The kv backend rejects properly on failure on both build targets
      // (webextension-polyfill wrapping chrome.storage.local, or IndexedDB).
      const result = await getKvStore().get(key);
      return (result[key] as StorageData<T> | undefined) || null;
    } catch (error) {
      const ctx = createLogContext('get', { key });
      const serializedError = serializeError(error as Error, ctx);
      logger.error('Failed to get value from storage', error as Error, {
        ...ctx,
        error: serializedError,
      });
      return null;
    }
  }

  /**
   * Sets multiple values in storage with batching
   */
  public async setMultiple(items: Record<string, any>): Promise<void> {
    // Each operation gets its own resolve/reject so processBatch settles every
    // one independently. Aggregating with Promise.all means the returned promise
    // only resolves once every write has actually completed, and rejects if any
    // single write fails — the previous shared-resolve approach could leave the
    // outer promise hanging forever or resolve before the write landed.
    const promises = Object.entries(items).map(
      ([key, value]) =>
        new Promise<void>((resolve, reject) => {
          this.metrics.totalOperations++;
          this.queueOperation({
            type: 'set',
            key,
            value,
            timestamp: Date.now(),
            resolve,
            reject,
          });
        })
    );

    await Promise.all(promises);
  }

  /**
   * Queues an operation for batching
   */
  private queueOperation(operation: StorageOperation): void {
    this.pendingOperations.push(operation);
    this.metrics.batchedOperations++;

    // If we've reached max batch size, process immediately
    if (this.pendingOperations.length >= this.options.maxBatchSize) {
      this.processBatch();
      return;
    }

    // Otherwise, set a timeout to process the batch
    if (this.batchTimeout === null) {
      this.batchTimeout = setTimeout(() => {
        this.processBatch();
      }, this.options.batchDelay);
    }
  }

  /**
   * Processes a batch of operations
   */
  private async processBatch(): Promise<void> {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    if (this.pendingOperations.length === 0) return;

    const operations = [...this.pendingOperations];
    this.pendingOperations = [];

    const ctx = createLogContext('processBatch', {
      operationCount: operations.length,
      types: operations.map(op => op.type),
    });

    this.metrics.totalBatches++;
    this.metrics.averageBatchSize =
      (this.metrics.averageBatchSize * (this.metrics.totalBatches - 1) + operations.length) /
      this.metrics.totalBatches;

    // The LAST operation enqueued for each key wins. Splitting into "all sets,
    // then all removes" reordered a remove-then-set into set-then-remove and
    // silently lost the later write.
    const finalOps = new Map<string, StorageOperation>();
    const opsByKey = new Map<string, StorageOperation[]>();
    for (const op of operations) {
      finalOps.set(op.key, op);
      const list = opsByKey.get(op.key) ?? [];
      list.push(op);
      opsByKey.set(op.key, list);
    }

    const setItems: Record<string, any> = {};
    const removeKeys: string[] = [];
    for (const op of finalOps.values()) {
      if (op.type === 'set') {
        setItems[op.key] = op.value;
      } else {
        removeKeys.push(op.key);
      }
    }

    try {
      if (Object.keys(setItems).length > 0) {
        await this.retryOperation(() => getKvStore().set(setItems));
      }
      if (removeKeys.length > 0) {
        await this.retryOperation(() => getKvStore().remove(removeKeys));
      }

      // Resolve all operations (superseded ops resolved too — their effect was
      // deliberately overwritten by a later operation on the same key).
      operations.forEach(op => {
        op.resolve();
        this.metrics.successfulOperations++;
      });

      logger.info('Batch processed successfully', {
        ...ctx,
        metrics: this.getMetrics(),
      });
    } catch (error) {
      const serializedError = serializeError(error as Error, ctx);
      logger.error('Failed to process batch, falling back to per-key writes', error as Error, {
        ...ctx,
        error: serializedError,
      });

      // One bad key (e.g. an oversized value hitting QUOTA_BYTES) must not fail
      // every unrelated key batched in the same window — retry each key alone
      // and settle its operations individually.
      for (const [key, finalOp] of finalOps.entries()) {
        const keyOps = opsByKey.get(key) ?? [];
        try {
          await this.retryOperation(() =>
            finalOp.type === 'set'
              ? getKvStore().set({ [key]: finalOp.value })
              : getKvStore().remove(key)
          );
          keyOps.forEach(op => {
            op.resolve();
            this.metrics.successfulOperations++;
          });
        } catch (keyError) {
          logger.error('Storage write failed for key', keyError as Error, {
            ...ctx,
            key,
            quotaExceeded: isQuotaError(keyError),
          });
          keyOps.forEach(op => {
            op.reject(keyError);
            this.metrics.failedOperations++;
          });
        }
      }
    }
  }

  /**
   * Retries an operation with exponential backoff
   */
  private async retryOperation(operation: () => Promise<void>): Promise<void> {
    let attempt = 0;
    let lastError: Error | null = null;

    while (attempt < this.options.retryAttempts) {
      try {
        await operation();
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        // Quota errors are permanent for the same payload — retrying burns
        // time and still fails. Surface immediately.
        if (isQuotaError(error)) {
          throw lastError;
        }
        attempt++;

        if (attempt < this.options.retryAttempts) {
          const delay = this.options.retryDelay * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError;
  }

  /**
   * Gets the current metrics
   */
  public getMetrics() {
    return {
      ...this.metrics,
      successRate:
        this.metrics.totalOperations > 0
          ? (this.metrics.successfulOperations / this.metrics.totalOperations) * 100
          : 100,
    };
  }

  /**
   * Flushes any pending operations
   */
  public async flush(): Promise<void> {
    if (this.pendingOperations.length > 0) {
      await this.processBatch();
    }
  }

  /**
   * Resets the manager's state
   */
  public reset(): void {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    this.pendingOperations = [];
    this.metrics = {
      totalOperations: 0,
      batchedOperations: 0,
      successfulOperations: 0,
      failedOperations: 0,
      averageBatchSize: 0,
      totalBatches: 0,
    };
  }
}
